/**
 * Wire shapes of `/v1/content-pages` (backend `modules/content_pages/content-pages.schema.ts`).
 * The contract is recorded in the workspace `docs/CONTRACTS.md` under "Content pages".
 */

export type ContentLocale = 'en' | 'ar';
export type ContentPageCategory = 'legal' | 'general';
export type ContentVersionStatus = 'current' | 'scheduled' | 'superseded';

export const CONTENT_LOCALES: readonly ContentLocale[] = ['en', 'ar'];
export const DEFAULT_CONTENT_LOCALE: ContentLocale = 'en';

/**
 * The slugs the application links to by name. They never change once created, so they are safe to
 * hardcode; any other page is discovered through `ContentPageService.listPages`.
 */
export const CONTENT_PAGE_SLUGS = {
    terms: 'terms-and-conditions',
    privacy: 'privacy-policy',
    about: 'about',
} as const;

export interface ContentSection {
    /** Stable anchor, identical in every language and across versions. */
    key: string;
    title: string;
    /** Restricted Markdown: no raw HTML, safe link schemes only. */
    body: string;
}

export interface ContentDocument {
    title: string;
    summary: string | null;
    sections: ContentSection[];
}

export type ContentTranslations = Partial<Record<ContentLocale, ContentDocument>>;
export type LocalizedText = Partial<Record<ContentLocale, string>>;

export interface ContentPageSummary {
    slug: string;
    category: ContentPageCategory;
    displayOrder: number;
    locale: ContentLocale;
    title: string;
    summary: string | null;
    version: number;
    effectiveAt: string;
    publishedAt: string;
}

export interface ContentPage extends ContentDocument {
    slug: string;
    category: ContentPageCategory;
    version: number;
    status: ContentVersionStatus;
    effectiveAt: string;
    publishedAt: string;
    /** The language actually served; differs from `requestedLocale` when the page lacks it. */
    locale: ContentLocale;
    requestedLocale: ContentLocale;
    availableLocales: ContentLocale[];
    changeSummary: string | null;
    upcoming: { version: number; effectiveAt: string } | null;
}

/** What a renderer needs besides the document, so the admin preview can pass none of it. */
export interface ContentPageMeta {
    version: number;
    status: ContentVersionStatus;
    effectiveAt: string;
    locale: ContentLocale;
    requestedLocale: ContentLocale;
    upcoming: { version: number; effectiveAt: string } | null;
}

export function isContentLocale(value: unknown): value is ContentLocale {
    return value === 'en' || value === 'ar';
}

export function contentDirection(locale: ContentLocale): 'ltr' | 'rtl' {
    return locale === 'ar' ? 'rtl' : 'ltr';
}
